import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import {
  DISTRESS_FLAGS,
  FLAG_LABELS,
  type SaveResponse,
  type UploadResponse,
  type Vlastnik,
} from '@/lib/types';
import { cn } from '@/lib/utils';

export default function Review() {
  const loc = useLocation();
  const navigate = useNavigate();
  const result = (loc.state as { result?: UploadResponse } | null)?.result;

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);

  async function save() {
    if (!result) return;
    setSaving(true);
    setSaveError(null);
    setDuplicateId(null);
    try {
      const data = await api<SaveResponse>('/api/properties', {
        method: 'POST',
        json: { parsed: result.parsed },
      });
      if ('status' in data && data.status === 'created') {
        navigate(`/properties/${data.propertyId}`);
        return;
      }
      // Should not reach — non-2xx throws ApiError.
      setSaveError('Neočekávaná odpověď serveru.');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const body = e.body as { existingId?: string; message?: string };
        setDuplicateId(body?.existingId || null);
        setSaveError(body?.message || 'Nemovitost už v DB existuje.');
      } else {
        setSaveError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setSaving(false);
    }
  }

  if (!result) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold">Žádná data k zobrazení</h1>
        <p className="text-sm text-muted-foreground">
          Tato stránka zobrazuje výsledek po nahrání PDF. Začni od{' '}
          <Link to="/upload" className="underline">
            uploadu
          </Link>
          .
        </p>
      </div>
    );
  }

  const { parsed, source } = result;
  const { nemovitost, vlastnici, pravoHospodaritStat, flags, omezeni, jineZapisy, ochrana, warnings } =
    parsed;

  return (
    <div className="space-y-6 max-w-4xl">
      <header className="space-y-1">
        <div className="flex items-baseline gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Zkontroluj a potvrď</h1>
          {source && (
            <span className="text-xs text-muted-foreground">
              z {source.filename} ({Math.round(source.size / 1024)} kB)
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Tohle vyparsoval parser. Než to uložím do databáze, projdi to. Distress flagy
          jsou červené, informativní oranžové.
        </p>
      </header>

      {flags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {flags.map((f) => (
            <span
              key={f}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium border',
                DISTRESS_FLAGS.has(f)
                  ? 'bg-destructive/10 border-destructive/40 text-destructive'
                  : 'bg-amber-100 border-amber-300 text-amber-900',
              )}
            >
              {FLAG_LABELS[f] || f}
            </span>
          ))}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="font-medium mb-1">Pozor:</div>
          <ul className="list-disc list-inside space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="rounded-lg border">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium">Nemovitost</div>
        <dl className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-1.5 p-4 text-sm">
          <Field label="Parcela" value={nemovitost.parcela} />
          <Field label="Obec" value={nemovitost.obec && `${nemovitost.obec} [${nemovitost.obecKod ?? '?'}]`} />
          <Field label="Katastrální území" value={nemovitost.ku && `${nemovitost.ku} [${nemovitost.kuKod ?? '?'}]`} />
          <Field label="Číslo LV" value={nemovitost.lv} />
          <Field label="Výměra" value={nemovitost.vymeraM2 ? `${nemovitost.vymeraM2} m²` : null} />
          <Field label="Druh pozemku" value={nemovitost.druhPozemku} />
          <Field label="Typ stavby" value={nemovitost.typStavby} />
          <Field label="Č. p." value={nemovitost.cp} />
          <Field label="Ulice" value={nemovitost.ulice} />
          <Field label="Adresní místo" value={nemovitost.adresniMisto} />
        </dl>
      </section>

      <section className="rounded-lg border">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium flex items-baseline justify-between">
          <span>Vlastníci</span>
          <span className="text-xs text-muted-foreground font-normal">{vlastnici.length} osob/subjektů</span>
        </div>
        <ul className="divide-y">
          {vlastnici.map((v, i) => (
            <VlastnikRow key={i} v={v} />
          ))}
          {vlastnici.length === 0 && (
            <li className="p-4 text-sm text-muted-foreground">Žádný vlastník nebyl rozpoznán.</li>
          )}
        </ul>
      </section>

      {pravoHospodaritStat.length > 0 && (
        <section className="rounded-lg border">
          <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium">
            Příslušnost hospodařit s majetkem státu
          </div>
          <ul className="divide-y">
            {pravoHospodaritStat.map((v, i) => (
              <VlastnikRow key={i} v={v} />
            ))}
          </ul>
        </section>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <ListBox title="Omezení vlastnického práva" items={omezeni} emptyText="Žádná" />
        <ListBox title="Jiné zápisy" items={jineZapisy} emptyText="Žádné" />
        <ListBox title="Způsob ochrany" items={ochrana} emptyText="Žádný" />
      </div>

      {saveError && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive space-y-2">
          <div>{saveError}</div>
          {duplicateId && (
            <Link
              to={`/properties/${duplicateId}`}
              className="inline-block text-sm underline font-medium"
            >
              Otevřít existující záznam →
            </Link>
          )}
        </div>
      )}

      <div className="flex items-center gap-3 pt-2 border-t">
        <button
          onClick={() => navigate('/upload')}
          className="px-4 py-2 rounded-md border text-sm hover:bg-muted"
          disabled={saving}
        >
          Zpět na upload
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Ukládám…' : 'Uložit do databáze'}
        </button>
        <span className="text-xs text-muted-foreground ml-auto font-mono">
          dedup: {parsed.dedupKey}
        </span>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={value ? '' : 'text-muted-foreground italic'}>{value || '—'}</dd>
    </>
  );
}

function VlastnikRow({ v }: { v: Vlastnik }) {
  return (
    <li className="p-3 flex items-start gap-3 text-sm">
      <div className="flex-1 min-w-0">
        <div className="font-medium flex items-center gap-2 flex-wrap">
          {v.name}
          {v.isSjm && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-100 text-blue-900 border border-blue-200">
              SJM
            </span>
          )}
          {v.isCompany && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-900 border border-purple-200">
              právnická
            </span>
          )}
          {v.isStat && (
            <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
              stát
            </span>
          )}
        </div>
        {v.address ? (
          <div className="text-xs text-muted-foreground mt-0.5">{v.address}</div>
        ) : (
          <div className="text-xs text-muted-foreground/70 italic mt-0.5">bez adresy</div>
        )}
      </div>
      <div className="font-mono text-sm tabular-nums shrink-0">{v.podil || '—'}</div>
    </li>
  );
}

function ListBox({ title, items, emptyText }: { title: string; items: string[]; emptyText: string }) {
  return (
    <div className="rounded-lg border">
      <div className="px-3 py-2 border-b bg-muted/40 text-xs font-medium uppercase tracking-wide">
        {title}
      </div>
      <ul className="p-3 text-sm space-y-1">
        {items.length === 0 ? (
          <li className="text-muted-foreground italic">{emptyText}</li>
        ) : (
          items.map((x, i) => <li key={i}>{x}</li>)
        )}
      </ul>
    </div>
  );
}
